/**
 * 동급 매칭 벤치마크 — 산점도 + 강조 (DESIGN §5.4 · §8.6.2 형태 선택표).
 *
 * **"전국 몇 등"이 아니다.** 동급은 같은 지역(zcode)·같은 충전기 타입·연식 ±2년
 * 조건을 만족하는 충전기를 보유한 사업자들이다 (백엔드 `scorecard_peers`).
 * 전국 146곳을 한 줄로 세우면 고속충전기만 있는 곳과 완속만 있는 곳이 같은 자로
 * 재어진다 — 그건 운영을 재는 게 아니라 설비 구성을 재는 것이다.
 *
 * **막대가 아니라 점인 이유.** 실측 동급 117곳 중 81곳이 가동률 0.98 위에 몰려
 * 있다. 0 에서 시작하는 막대는 전부 같은 길이로 보이고, 축을 잘라 확대한 막대는
 * 길이비를 거짓말한다. 점은 면적이 없으므로 축을 확대해도 왜곡이 없다 (§8.6.3).
 *
 * **세로축에 동급 충전기 수를 준 이유.** 가동률만으로 한 줄에 흩뿌리면 오른쪽
 * 끝에서 점이 서로 겹쳐 몇 곳인지 셀 수 없다. 세로로 관측량을 주면 겹침이
 * 풀리는 동시에 "저 100%는 몇 기짜리인가"가 같은 그림 안에서 읽힌다 — 충전기
 * 1기짜리의 100%와 8만기짜리의 99%를 나란히 놓지 않기 위한 장치다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import { CHART_H, axisStyle, niceBand } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct, pctAxis } from '@/lib/fmt'
import { useEntityLabel } from '@/lib/labels'
import type { Envelope } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

/** `/scorecard/{busi_id}` 의 `data.peers` 한 줄. */
export interface Peer {
  busi_id: string
  /** **그 사업자의 전체 대수가 아니다** — 동급 조건에 걸린 충전기만 센 값이다 */
  n_chargers: number
  availability: number
  availability_ci: [number, number]
}

/** `/scorecard/{busi_id}` 의 `data.profile`. `quality_operator` 한 줄 그대로다. */
export interface ScorecardProfile {
  busi_id: string
  busi_nm: string | null
  n_chargers: number
  na_rate: number
  stale_rate: number
  upddt_error_rate: number
  distinct_codes: number
  quality: number
  comparable: boolean
  reasons: string | null
}

export interface ScorecardData {
  profile: ScorecardProfile
  peers: Peer[]
  availability: number | null
  peer_percentile: number | null
}

/**
 * 백분위를 신뢰할 수 있는 최소 동급 수.
 *
 * 백분위의 해상도는 1/n 이다. n=20 이면 동급 한 곳이 위아래로 자리를 바꿀 때마다
 * 값이 5포인트씩 통째로 뛴다 — 그보다 성기면 "62백분위"의 소수 자리를 읽을 근거가
 * 없다. 그래서 20 을 하한으로 두되 **미달이어도 값을 지우지 않는다.** 지우면
 * 왜 없는지 물어볼 곳이 없으므로, 값 옆에 "신뢰하기 어렵다"를 함께 인쇄한다.
 */
const PEER_MIN = 20

/**
 * 가로축에서 잘라낼 하위 꼬리의 비율.
 *
 * 실측 동급의 중위 가동률은 99.5% 인데 최저는 0% 다. 0 부터 그리면 관심 구간
 * (99%대)이 전체 폭의 1% 로 눌려 본인 위치를 읽을 수 없다. 하위 10% 는 축 밖으로
 * 보내되 **이름과 값을 각주에 적는다** (§8.6.3 규칙 4) — 잘라내되 조용히 버리지
 * 않는다. 본인 신뢰구간은 어떤 경우에도 축 안에 남긴다.
 */
const TAIL_Q = 0.1

export function PeerBenchmark({ busiId }: { busiId: string }) {
  const filters = useFilters()
  const labelOf = useEntityLabel()

  // 프로파일 카드와 **같은 쿼리 키**다 — 같은 응답을 두 번 받아 오지 않는다
  const q = useQuery({
    queryKey: ['scorecard', busiId, filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<ScorecardData>(
        `/scorecard/${encodeURIComponent(busiId)}`,
        toQueryFilters(filters),
        {},
        signal,
      ),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<ScorecardData> | undefined
  const peers = useMemo(() => env?.data.peers ?? [], [env])
  const self = peers.find((p) => p.busi_id === busiId) ?? null
  const percentile = env?.data.peer_percentile ?? null
  const n = peers.length
  const selfName = labelOf('busi', busiId)

  /**
   * 차트를 그릴 수 있는가 — **본인 점이 없으면 아무 마크도 없다.**
   *
   * 백엔드는 관측 시간이 0 인 사업자를 동급 목록에서 빼므로 (analysis.py 의
   * `t_obs <= 0: continue`), 실측 146곳 중 25곳은 자기 자신이 목록에 없다.
   * 그때는 "본인과 겹치는가"·"차트에 있는가"를 물을 수 없다 — 이 값을 그대로
   * '겹치지 않음'·'표시'로 채우면 **하지 않은 비교를 한 척**이 된다.
   */
  const drawn = self != null

  /** 본인 구간과 겹치는 동급 — 이들과는 순위를 주장할 수 없다 */
  const overlap = useMemo(() => {
    if (!self) return new Set<string>()
    const [lo, hi] = self.availability_ci
    return new Set(
      peers
        .filter(
          (p) =>
            p.busi_id !== busiId && p.availability_ci[0] <= hi && p.availability_ci[1] >= lo,
        )
        .map((p) => p.busi_id),
    )
  }, [peers, self, busiId])

  /**
   * 본인과 값이 정확히 같은 동급 수.
   *
   * 백분위는 "나보다 **낮은** 곳의 비율"이라 동점을 아래로 세지 않는다. 실측에서
   * 가동률이 정확히 100% 인 사업자가 동급 13곳 중 자기 말고도 9곳이나 되는 경우가
   * 있었는데, 그때 백분위는 23.1 로 찍힌다 — 최고값인데 하위처럼 보인다.
   * 이 함정을 숫자로 드러낸다.
   */
  const tied = useMemo(() => {
    if (!self) return 0
    return peers.filter(
      (p) => p.busi_id !== busiId && Math.abs(p.availability - self.availability) < 1e-12,
    ).length
  }, [peers, self, busiId])

  const median = useMemo(() => {
    if (peers.length === 0) return null
    const v = peers.map((p) => p.availability).sort((a, b) => a - b)
    const mid = Math.floor(v.length / 2)
    if (v.length % 2 === 1) return v[mid] ?? null
    return ((v[mid - 1] ?? 0) + (v[mid] ?? 0)) / 2
  }, [peers])

  /** 가로축 구간. 각주가 "지금 어디까지 확대했나"를 말해야 하므로 밖으로 꺼낸다 */
  const band = useMemo(() => {
    if (peers.length === 0) return { min: 0, max: 1, interval: 0.2 }
    const v = peers.map((p) => p.availability).sort((a, b) => a - b)
    const tail = v[Math.floor(v.length * TAIL_Q)] ?? v[0] ?? 0
    // 수염을 그리는 대상(본인 · 겹치는 동급)의 구간 하한은 무슨 일이 있어도 축
    // 안에 넣는다. 밖으로 나가면 수염이 세로축 라벨 위를 지나간다
    const marked = [
      ...(self ? [self.availability_ci[0]] : []),
      ...peers.filter((p) => overlap.has(p.busi_id)).map((p) => p.availability_ci[0]),
    ]
    const lo = Math.min(tail, ...marked)
    const hi = Math.max(v[v.length - 1] ?? 1, self?.availability_ci[1] ?? 0)
    const pad = Math.max((hi - lo) * 0.06, 0.0005)
    return niceBand(Math.max(0, lo - pad), Math.min(1, hi + pad))
  }, [peers, self, overlap])

  /** 축 밖으로 나간 동급 — 지우지 않고 이름과 값을 각주에 적는다 */
  const offAxis = useMemo(
    () => peers.filter((p) => p.availability < band.min),
    [peers, band],
  )

  const option = useMemo<EChartsOption>(() => {
    const accent = cssVar('--c-accent', '#0066cc')
    const cat2 = cssVar('--c-cat-2', '#a35200')
    const na = cssVar('--c-na', '#6f6f75')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const canvas = cssVar('--c-canvas', '#ffffff')

    const byId = new Map(peers.map((p) => [p.busi_id, p]))
    // 수염을 그리는 대상 = 본인 + 본인과 구간이 겹치는 동급.
    // 117곳 전부에 수염을 그리면 화면이 수염으로 덮여 아무것도 안 읽힌다
    // **본인을 마지막에 둔다.** ECharts 는 데이터 순서대로 그리므로 앞에 두면
    // 겹치는 동급이 그 위에 덮인다 — 강조하려던 점이 배경에 가려진다
    const marked = [
      ...peers.filter((p) => overlap.has(p.busi_id)).map((p) => ({ p, isSelf: false })),
      ...(self ? [{ p: self, isSelf: true }] : []),
    ]
    const background = peers.filter(
      (p) => p.busi_id !== busiId && !overlap.has(p.busi_id) && p.availability >= band.min,
    )

    return {
      // 가동률 100% 는 실측에서 가장 흔한 값이라 점이 축 오른쪽 끝에 쌓인다.
      // 오른쪽 여백이 좁으면 그 점들이 반쪽만 그려진다 (아래 clip:false 와 한 쌍)
      grid: { left: 68, right: 40, top: 20, bottom: 30 },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const raw = (params as { data: unknown }).data
          const arr = Array.isArray(raw) ? (raw as unknown[]) : []
          const peer = byId.get(String(arr[arr.length - 1] ?? ''))
          if (!peer) return ''
          const mine = peer.busi_id === busiId
          return [
            `<b>${labelOf('busi', peer.busi_id)}</b>${mine ? ' (선택한 사업자)' : ''}`,
            `가동률 ${pct(peer.availability)}`,
            `95% 구간 ${pct(peer.availability_ci[0])} ~ ${pct(peer.availability_ci[1])}`,
            `동급 충전기 ${int(peer.n_chargers)}기`,
          ].join('<br/>')
        },
      },
      xAxis: {
        type: 'value',
        min: band.min,
        max: band.max,
        interval: band.interval,
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: pctAxis(band.interval * 5) },
      },
      yAxis: {
        // 동급 대수가 1기~8만기로 벌어진다. 선형 축이면 하위 100곳이 바닥에
        // 눌려 한 줄이 된다 — 로그를 쓰고 그 사실을 눈금 설명에 적는다 (§8.6.3)
        type: 'log',
        logBase: 10,
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: (v: number) => int(v) },
      },
      series: [
        {
          // 배경 동급 — 회색 빈 원. 크기로는 아무것도 말하지 않는다
          // (대수는 이미 세로축이 말하므로 같은 것을 두 번 인코딩하지 않는다)
          type: 'scatter',
          symbolSize: 7,
          // 값이 정확히 100% 인 점은 축 경계에 놓인다 — 잘라내면 "100% 가 몇
          // 곳인가"가 반쪽 원으로 보인다. 축 밖 데이터는 이미 걸러 두었다
          clip: false,
          itemStyle: { color: 'transparent', borderColor: na, borderWidth: 1.2 },
          data: background.map((p) => [p.availability, p.n_chargers, p.busi_id]),
        },
        {
          type: 'custom',
          clip: false,
          // [가동률, 동급대수, 구간하한, 구간상한, 본인여부, 사업자코드]
          data: marked.map(({ p, isSelf }) => [
            p.availability,
            p.n_chargers,
            p.availability_ci[0],
            p.availability_ci[1],
            isSelf ? 1 : 0,
            p.busi_id,
          ]),
          encode: { x: 0, y: 1 },
          renderItem: (_params, api) => {
            const y = api.value(1) as number
            const mine = api.value(4) === 1
            const mid = api.coord([api.value(0) as number, y])
            const cx = mid[0] ?? 0
            const cy = mid[1] ?? 0
            const xLo = api.coord([api.value(2) as number, y])[0] ?? 0
            const xHi = api.coord([api.value(3) as number, y])[0] ?? 0
            const color = mine ? accent : cat2
            const r = mine ? 9 : 6
            const cap = Math.max(3, r - 3)
            const whisker = { stroke: color, lineWidth: mine ? 2 : 1.5 }
            return {
              type: 'group',
              children: [
                { type: 'line', shape: { x1: xLo, y1: cy, x2: xHi, y2: cy }, style: whisker },
                {
                  type: 'line',
                  shape: { x1: xLo, y1: cy - cap, x2: xLo, y2: cy + cap },
                  style: whisker,
                },
                {
                  type: 'line',
                  shape: { x1: xHi, y1: cy - cap, x2: xHi, y2: cy + cap },
                  style: whisker,
                },
                {
                  type: 'circle',
                  shape: { cx, cy, r },
                  // 본인은 채운 점, 겹치는 동급은 빈 점 — 색만으로 구분하지 않는다
                  style: mine
                    ? { fill: accent, stroke: canvas, lineWidth: 2 }
                    : { fill: canvas, stroke: cat2, lineWidth: 2 },
                },
              ],
            }
          },
          markLine:
            median != null
              ? {
                  silent: true,
                  symbol: 'none',
                  lineStyle: { color: ink3, type: 'solid', width: 1 },
                  // 중위가 99% 대라 선이 오른쪽 끝에 선다 — 인라인 라벨을 붙이면
                  // 잘리거나 세로로 눕는다. 값은 범례에 적어 두었다
                  label: { show: false },
                  data: [{ xAxis: median }],
                }
              : undefined,
        },
      ],
    }
  }, [peers, self, overlap, median, band, busiId, labelOf])

  /**
   * 백분위 문장 — **차트 뷰와 표 뷰 양쪽에 같이 둔다.**
   * 본문에만 두면 표로 전환하는 순간 "동급이 몇 곳인지"가 사라진다.
   */
  const summary = (
    <div className="peer__summary">
      <p className="peer__line">
        <strong className="num">동급 {int(n)}곳</strong> 가운데{' '}
        {percentile != null ? (
          <>
            <strong className="num">{pct(percentile, 1)}</strong>가 {selfName} 보다 낮습니다
          </>
        ) : (
          <strong>백분위를 계산할 수 없습니다</strong>
        )}
        {tied > 0 && <span className="peer__tied"> · 값이 같은 동급 {int(tied)}곳</span>}
      </p>
      <p className="peer__hint">
        동급 = 같은 지역·충전기 타입·연식(±2년) 조건을 공유하는 사업자입니다.{' '}
        <b>전국 사업자 전체를 한 줄로 세운 순위가 아닙니다.</b>
      </p>
      {/* 왜 못 내는지는 차트 자리에만 적으면 안 된다 — 표로 바꾸면 그 문장이 사라진다 */}
      {!drawn && (
        <p className="peer__hint">
          동급 집합 안에서 {selfName} 의 관측 시간이 0 이라 가동률도 신뢰구간도 없습니다. 0 으로
          채우지 않고 비워 둡니다.
        </p>
      )}
      {/* 백분위 자체가 없으면 "백분위가 흔들린다"는 경고도 할 말이 없다 */}
      {percentile != null && n > 0 && n < PEER_MIN && (
        <p className="peer__warn" role="status">
          동급이 {int(n)}곳뿐이라 <b>백분위를 신뢰하기 어렵습니다</b> — 한 곳이 자리를 바꾸면
          값이 {pct(1 / n, 1)}씩 통째로 움직입니다 (기준 {PEER_MIN}곳).
        </p>
      )}
    </div>
  )

  const ranked = useMemo(
    () => [...peers].sort((a, b) => b.availability - a.availability),
    [peers],
  )

  const table =
    peers.length > 0 ? (
      <>
        {summary}
        <div className="ctable">
          <table className="data-table">
            <caption className="sr-only">
              동급 사업자 {n}곳의 가동률과 95% 신뢰구간 (가동률 내림차순)
            </caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">사업자</th>
                <th scope="col" className="ta-r">
                  동급 충전기
                </th>
                <th scope="col" className="ta-r">
                  가동률
                </th>
                <th scope="col" className="ta-r">
                  95% 구간
                </th>
                {/* 본인 값이 없으면 이 두 칸은 물음 자체가 성립하지 않는다.
                    '겹치지 않음'·'표시'로 채우는 대신 칸을 두지 않는다 */}
                {drawn && <th scope="col">본인과 구간 겹침</th>}
                {drawn && <th scope="col">차트 표시</th>}
              </tr>
            </thead>
            <tbody>
              {ranked.map((p, i) => (
                <tr key={p.busi_id} className={p.busi_id === busiId ? 'peer__self' : ''}>
                  <td className="num">{i + 1}</td>
                  <td>
                    {labelOf('busi', p.busi_id)}
                    {p.busi_id === busiId && <span className="peer__me">선택</span>}
                  </td>
                  <td className="num ta-r">{int(p.n_chargers)}</td>
                  <td className="num ta-r">{pct(p.availability)}</td>
                  <td className="num ta-r">
                    {pct(p.availability_ci[0])} ~ {pct(p.availability_ci[1])}
                  </td>
                  {drawn && (
                    <td>
                      {p.busi_id === busiId ? '—' : overlap.has(p.busi_id) ? '겹침' : '겹치지 않음'}
                    </td>
                  )}
                  {drawn && <td>{p.availability < band.min ? '축 밖' : '표시'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!drawn && (
          <p className="peer__hint">
            본인 값이 없어 &lsquo;본인과 구간 겹침&rsquo;·&lsquo;차트 표시&rsquo; 칸을 두지
            않았습니다 — 하지 않은 비교를 &lsquo;겹치지 않음&rsquo;으로 채우지 않습니다.
          </p>
        )}
      </>
    ) : undefined

  return (
    <ChartFrame
      title="동급 매칭 벤치마크"
      question={`${selfName} — 같은 지역·타입·연식 조건의 동급 안에서 어디쯤인가`}
      meta={env?.meta}
      headline={n > 0 ? <Headline value={`${int(n)}곳`} unit="동급 사업자 수" /> : undefined}
      // 점을 하나도 그리지 않은 상태에서 "채운 점으로 강조했다"고 쓰면 화면에 없는
      // 마크를 설명하게 된다 — 그때는 아래 눈금 설명이 가정법임을 먼저 밝힌다
      reading={
        drawn
          ? `같은 지역·충전기 타입·연식(±2년) 조건을 공유하는 동급 사업자들을 점으로 흩뿌리고, ${selfName} 만 채운 점으로 강조했습니다. 오른쪽일수록 가동률이 높고, 위로 갈수록 동급 조건에 걸린 충전기가 많습니다. 전국을 한 줄로 세운 순위가 아니라 조건을 맞춘 뒤의 위치입니다.`
          : `${selfName} 의 관측 시간이 0 이라 지금은 점을 하나도 그리지 않았습니다 — 아래 눈금 설명은 본인 값이 있을 때 무엇을 어떻게 그리는지를 말합니다. 동급 ${int(n)}곳의 값 자체는 표 보기에 그대로 있습니다.`
      }
      encoding={[
        {
          role: '가로축',
          what: '가동률 (%)',
          detail: `관측된 시간 중 UP 이었던 비율입니다. 관측되지 않은 시간은 분모에 넣지 않습니다. 0% 부터 그리지 않고 ${pct(band.min, 0)}~${pct(band.max, 0)} 구간만 확대했는데, 동급의 중위가 ${pct(median, 1)} 라 0 부터 그리면 모든 점이 오른쪽 끝 한 자리에 겹치기 때문입니다. 점은 면적이 없어 축을 잘라도 길이가 거짓말하지 않습니다 — 같은 이유로 막대를 쓰지 않았습니다.`,
        },
        {
          role: '세로축',
          what: '동급 충전기 수 (로그 눈금)',
          detail:
            '동급 조건에 걸린 충전기 수이며 그 사업자의 전체 대수가 아닙니다. 1기부터 8만기까지 벌어져 선형 축이면 하위 대부분이 바닥에 눌려 한 줄이 됩니다. 로그를 쓰면 겹침이 풀리고 "저 100%가 몇 기짜리인가"가 같은 그림에서 읽힙니다. 정확한 개수는 표 보기에 있습니다.',
        },
        {
          role: '점 채움',
          what: '선택한 사업자 · 구간이 겹치는 동급 · 나머지',
          detail:
            '채운 점이 선택한 사업자입니다. 색만으로 구분하지 않도록 채움/빈 원을 함께 씁니다. 빈 원 중 진한 테두리는 본인과 신뢰구간이 겹치는 동급이며, 그들과는 순위를 주장할 수 없습니다.',
        },
        {
          role: '수염',
          what: '95% Wilson 신뢰구간',
          detail:
            '관측 시간(분)을 시행 횟수로 쓴 Wilson 구간입니다. 본인과 "본인과 겹치는 동급"에만 그렸습니다 — 동급 전부에 그리면 화면이 수염으로 덮여 아무것도 읽히지 않습니다. 모든 동급의 정확한 구간 폭은 표 보기에 있습니다.',
        },
        {
          role: '세로선',
          what: '동급 중위 가동률',
          detail:
            '평균이 아니라 중위입니다. 동급에는 가동률 0% 인 곳이 섞여 있어 평균은 그 한 곳에 끌려갑니다.',
        },
      ]}
      /* 차트를 못 그리는 상태에서 범례만 남으면 화면에 없는 마크를 찾게 된다 */
      legend={
        drawn
          ? [
              { label: '선택한 사업자', color: cssVar('--c-accent', '#0066cc'), pattern: 'dot' },
              {
                label: '구간이 겹치는 동급 — 순위를 주장할 수 없다',
                color: cssVar('--c-cat-2', '#a35200'),
                pattern: 'ring',
              },
              { label: '나머지 동급', color: cssVar('--c-na', '#6f6f75'), pattern: 'ring' },
              {
                label: `동급 중위 ${pct(median)}`,
                color: cssVar('--c-ink-3', '#626266'),
                pattern: 'line',
              },
            ]
          : undefined
      }
      isLoading={q.isLoading}
      error={q.error as Error | null}
      onRetry={() => void q.refetch()}
      table={table}
      notes={[
        {
          label: '동급은 전국이 아니다.',
          text: `같은 지역(zcode)·충전기 타입·연식 ±2년 조건을 만족하는 충전기를 가진 사업자만 모은 ${int(n)}곳입니다. 전국 전체를 한 줄로 세운 값과는 다른 숫자이며, 조건을 통제했기 때문에 다른 것이 정상입니다.`,
        },
        percentile != null
          ? {
              label: '백분위는 동급 수에 흔들린다.',
              text: `백분위의 해상도는 1/n 입니다. 지금 n=${int(n)} 이므로 동급 한 곳이 자리를 바꿀 때마다 ${pct(n > 0 ? 1 / n : 0, 1)}씩 움직입니다. ${
                n < PEER_MIN ? `기준 ${PEER_MIN}곳에 못 미쳐 이 백분위는 신뢰하기 어렵습니다.` : `기준 ${PEER_MIN}곳은 넘겼습니다.`
              }`,
            }
          : {
              label: '백분위가 아예 없다.',
              text: `${selfName} 의 관측 시간이 0 이라 가동률을 계산할 수 없고, 따라서 동급 안의 위치도 없습니다. "0백분위"가 아니라 "잴 수 없다"입니다 — 아래 표의 동급 ${int(n)}곳은 그대로 보여주되 본인 줄은 없습니다.`,
            },
        tied > 0
          ? {
              label: '동점을 백분위가 삼킨다.',
              text: `백분위는 "나보다 낮은 곳의 비율"이라 값이 같은 곳을 아래로 세지 않습니다. 지금 값이 같은 동급이 ${int(tied)}곳이므로 백분위가 실제 위치보다 낮게 나옵니다.`,
            }
          : '',
        {
          label: '구간이 겹치면 순위를 주장할 수 없다.',
          // 본인 구간이 없을 때 "겹치는 동급은 없다"고 쓰면 하지 않은 비교를
          // 통과한 것처럼 말하게 된다 — 그때는 비교가 성립하지 않는다고 쓴다
          text: !drawn
            ? '본인의 관측 시간이 0 이라 신뢰구간 자체가 없습니다. 어느 동급과 겹치는지도, 겹치지 않는지도 말할 수 없습니다 — 표에서도 그 칸을 비웠습니다.'
            : overlap.size > 0
              ? `본인과 95% 구간이 겹치는 동급이 ${int(overlap.size)}곳입니다. 이들과의 앞뒤는 관측이 더 쌓이면 뒤집힐 수 있습니다.`
              : '본인과 95% 구간이 겹치는 동급은 없습니다. 다만 이는 "관측된 범위 안에서"이며 관측 창 자체가 짧다는 사실은 그대로입니다.',
        },
        // 차트가 없는 상태에서 "축 밖으로 나갔다"는 말은 있지도 않은 축을 가리킨다
        drawn && offAxis.length > 0
          ? {
              label: '축 밖으로 나간 동급을 이름으로 남긴다.',
              text: `가로축을 ${pct(band.min, 0)} 부터 그렸으므로 그보다 낮은 ${int(offAxis.length)}곳은 차트에 없습니다: ${offAxis
                .map((p) => `${labelOf('busi', p.busi_id)} ${pct(p.availability, 1)}`)
                .join(' · ')}. 표 보기에는 전부 있습니다.`,
            }
          : '',
        {
          label: '동급 충전기 수는 그 사업자의 전체 대수가 아니다.',
          text: '동급 조건(지역·타입·연식)에 걸린 충전기만 센 값입니다. 프로파일 카드의 충전기 수와 다를 수 있으며, 다른 것이 정상입니다.',
        },
      ]}
    >
      {peers.length === 0 || self == null ? (
        <div className="verdict-note" role="status">
          <p>
            <strong>{selfName} 의 위치를 찍을 수 없습니다.</strong>{' '}
            {peers.length === 0
              ? '같은 지역·타입·연식 조건을 공유하는 동급 사업자가 잡히지 않았습니다.'
              : '동급 집합 안에서 이 사업자의 관측 시간이 0 이라 가동률을 계산할 수 없습니다.'}
          </p>
          <p className="verdict-note__hint">
            "값이 0"이 아니라 <em>"계산할 근거가 없다"</em>입니다. 0 으로 채워 넣지 않습니다.
          </p>
        </div>
      ) : (
        <>
          {summary}
          <EChart
            option={option}
            height={CHART_H}
            ariaLabel={`동급 매칭 벤치마크 산점도. 동급 ${n}곳 중 ${selfName} 의 가동률은 ${pct(
              self.availability,
            )}, 95% 구간 ${pct(self.availability_ci[0])}~${pct(
              self.availability_ci[1],
            )}, 동급 중위는 ${pct(median)} 입니다. 정확한 값은 표 보기에 있습니다.`}
          />
        </>
      )}
    </ChartFrame>
  )
}
